import { useState, useEffect, useCallback, useMemo } from "react";
import { 
  TrendingUp, TrendingDown, Phone, Calendar, Target, Sun, Cloud, 
  CloudRain, Snowflake, AlertTriangle, CheckCircle2, Coffee, Sparkles,
  ChevronLeft, ChevronRight, Pause, Play, DollarSign, Clock, Award,
  PhoneOff, Zap, Heart, Star, PartyPopper, Trophy, Flame, ThumbsUp,
  Users, Crown, Rocket, Battery, BatteryLow, TrendingDown as SlowDown,
  FileText, Eye, Clipboard, ArrowRight, Medal, Gift
} from "lucide-react";
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const SANIA_LOGO = "https://customer-assets.emergentagent.com/job_telecenter-hub/artifacts/dlcoh4rw_ChatGPT%20Image%206%20fe%CC%81vr.%202026%2C%2022_55_27.png";

// ==================== MESSAGES PAR DÉFAUT (fallback) ====================

// Blagues sur les personnes âgées - valeurs par défaut
const DEFAULT_BLAGUES_VIEUX = [
  "Pourquoi Mamie a mis son dentier au frigo ? Elle voulait un sourire rafraîchissant ! 😄",
  "Papy dit qu'à son âge, il a trois avantages : la sagesse, l'expérience... et il a oublié le troisième ! 🧓",
  "Mamie a installé WhatsApp. Elle envoie maintenant des vocaux de 15 minutes pour dire 'OK' ! 📱",
  "Papy dit que le secret de sa longévité, c'est de ne jamais courir pour attraper un bus. Le prochain arrive dans 10 minutes ! 🚌",
  "Une mamie au téléphone : 'Allô ? Parlez plus fort, j'ai mis mes lunettes !' 👓",
  "Papy regarde son smartphone : 'De mon temps, on devait attendre 3 jours pour recevoir une lettre. Maintenant je m'énerve si le message met 3 secondes !' ⏰",
  "Mamie a acheté un robot aspirateur. Elle lui a tricoté un petit pull pour l'hiver ! 🧹",
  "Pourquoi les seniors font les meilleurs clients ? Parce qu'ils ont tout leur temps pour nous écouter ! ❤️",
  "Papy au téléphone : 'Je rappelle plus tard, là je suis en train de regarder ma série... depuis 1987' 📺",
  "Mamie dit qu'elle fait du yoga. En fait, elle essaie juste de ramasser ses lunettes qui sont tombées 🧘",
];

// Messages LOI DES NOMBRES - valeurs par défaut
const DEFAULT_MESSAGES_LOI_NOMBRES = [
  "📊 LA LOI DES NOMBRES : Plus tu passes d'appels, plus tu prends de RDV ! C'est mathématique ! 🎯",
  "🔢 Rappel : Dans ce métier, la LOI DES NOMBRES est la clé ! Chaque appel te rapproche d'un RDV ! 💰",
  "📈 100 appels = X RDV garantis ! La persévérance PAIE TOUJOURS ! Continue d'appeler ! 📞",
  "🎲 Les statistiques sont de ton côté : plus tu appelles, plus tu gagnes ! C'est la règle d'or ! 🏆",
  "💡 Secret du succès : la QUANTITÉ mène à la QUALITÉ ! Multiplie tes appels ! 🚀",
];

// Messages rappels CONFIRMATION RDV - valeurs par défaut
const DEFAULT_MESSAGES_CONFIRMATION_RDV = [
  "📞 RAPPEL : Un RDV non confirmé = un lapin potentiel ! Prends 2 min pour confirmer ! ✅",
  "⚠️ N'oublie pas : CONFIRMER ses RDV, c'est S'ASSURER ses commissions ! 💰",
  "🎯 Un bon télépro confirme TOUJOURS ses RDV ! C'est la base du professionnalisme ! 👔",
  "📋 Petit rappel : la confirmation, c'est ce qui fait la différence entre un RDV et un échec ! ✨",
];

// Messages rappels CONTROLE QUALITÉ
const MESSAGES_CQ = [
  "⏰ RAPPEL CQ : Plus vite tu fais ton contrôle qualité, plus vite le dossier avance ! 🚀",
  "📝 Les CQ rapides = Dossiers qui avancent = Clients satisfaits = Primes ! 💵",
  "🎯 Un CQ fait dans les 48h, c'est un dossier qui ne traîne pas ! Pense à tes stats ! 📊",
  "⚡ CQ express = AMO content = Chantier rapide = Tout le monde gagne ! 🏆",
];

// Messages PAUSE - valeurs par défaut
const DEFAULT_MESSAGES_PAUSE = [
  "☕ Wow, tu as passé beaucoup d'appels ! Prends 5 min pour souffler, tu l'as mérité ! 🧘",
  "🌟 Belle activité téléphonique ! N'oublie pas de faire une petite pause, recharge tes batteries ! 🔋",
  "💪 Tu enchaînes les appels comme une championne ! Pause café bien méritée ? ☕",
  "🎉 Super rythme ! Mais pense à te reposer 5 min, un télépro reposé est un télépro efficace ! 😊",
];

// Messages PERTE DE VITESSE
const MESSAGES_PERTE_VITESSE = [
  "⚠️ Attention ! Cette semaine tu as {current} RDV vs {last} la semaine dernière... Tu perds de la vitesse ! 🏃‍♀️💨",
  "📉 Alerte : {current} RDV cette semaine vs {last} la semaine dernière. Il faut accélérer ! 🚀",
  "😟 Tu étais à {last} RDV à ce stade la semaine dernière, aujourd'hui {current}... Réveille-toi ! ⚡",
  "🔴 Comparatif : Semaine dernière {last} RDV, cette semaine {current}. L'argent attend pas ! 💰",
];

// Messages BONNE SEMAINE
const MESSAGES_BONNE_SEMAINE = [
  "🔥 Tu as {current} RDV vs {last} la semaine dernière ! Tu es en avance, CONTINUE ! 💪",
  "🚀 Incroyable ! {current} RDV cette semaine vs {last} la semaine passée ! Tu cartonne ! 🏆",
  "⭐ Tu dépasses déjà la semaine dernière ({current} vs {last}) ! La prime se rapproche ! 💰",
];

// Messages RECORD
const MESSAGES_RECORD = [
  "🏆 HISTORIQUE ! Tu approches du RECORD de {record} RDV en une semaine ! Tu en es à {current} ! 🔥🔥🔥",
  "👑 Attention RECORD en vue ! Meilleure semaine ever : {record} RDV. Toi cette semaine : {current} ! GO ! 🚀",
  "🎯 Le record à battre : {record} RDV. Tu es à {current}... C'est jouable ! FONCE ! 💪",
];

// Messages LEADER
const MESSAGES_LEADER = [
  "👑 {name} est EN TÊTE cette semaine avec {rdv} RDV et {appels} appels ! Qui va la détrôner ? 🏆",
  "🥇 Leader de la semaine : {name} ({rdv} RDV, {appels} appels) ! La course est lancée ! 🏃‍♀️",
  "🔥 {name} domine le classement : {rdv} RDV pour {appels} appels ! À toi de jouer ! 💪",
];

// Messages PIPELINE
const MESSAGES_PIPELINE = {
  transmisAMO: [
    "📤 La semaine dernière, {count} dossiers ont été transmis aux AMO ! Le pipeline avance ! 🚀",
    "✅ {count} dossiers envoyés aux AMO la semaine passée ! Ton travail porte ses fruits ! 🌟",
  ],
  vtAVenir: [
    "📅 {count} visites techniques planifiées à venir ! Les AMO comptent sur toi ! 🏠",
    "🔜 {count} VT programmées ! Chaque VT = un chantier potentiel = 💰 pour tous !",
  ],
  r2Semaine: [
    "📋 {count} R2 effectués la semaine dernière ! Le cycle continue ! 🔄",
    "✨ {count} R2 la semaine passée ! Les dossiers avancent grâce à toi ! 🎯",
  ],
  r2AVenir: [
    "📆 {count} R2 planifiés à venir ! Les commerciaux sont en route ! 🚗",
    "🎯 {count} R2 en approche ! Chaque R2 confirmé = projet qui se concrétise ! 💪",
  ],
};

// ==================== COMPOSANT ====================

const WeatherIcon = ({ condition, className }) => {
  switch (condition) {
    case 'clear': return <Sun className={className} />;
    case 'clouds': return <Cloud className={className} />;
    case 'rain': return <CloudRain className={className} />;
    case 'snow': return <Snowflake className={className} />;
    default: return <Sun className={className} />;
  }
};

function SanIABanner({ ringoverStats, dashboardData, telepros, allClients, authUser }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [weather, setWeather] = useState(null);
  const [showBlague, setShowBlague] = useState(false);
  const [currentBlague, setCurrentBlague] = useState(0);
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());
  
  // Extraire l'alias télépro de l'utilisateur connecté
  const userTelepro = authUser?.aliases?.telepro || null;
  const isPersonalized = !!userTelepro;
  
  // Filtrer les clients selon l'alias de l'utilisateur
  const filteredClients = useMemo(() => {
    if (!allClients) return [];
    if (!userTelepro) return allClients; // Si pas d'alias, afficher tout
    
    // Filtrer les clients où le télépro correspond à l'alias de l'utilisateur
    return allClients.filter(client => {
      const clientTelepro = client.telepro?.trim().toUpperCase();
      const aliasTelepro = userTelepro.trim().toUpperCase();
      return clientTelepro === aliasTelepro;
    });
  }, [allClients, userTelepro]);
  
  // Données du dashboard filtrées
  const personalizedDashboardData = useMemo(() => {
    if (!dashboardData?.clients) return { clients: filteredClients };
    if (!userTelepro) return dashboardData;
    
    return {
      ...dashboardData,
      clients: filteredClients
    };
  }, [dashboardData, filteredClients, userTelepro]);
  
  // État pour les prompts dynamiques depuis l'API
  const [dynamicPrompts, setDynamicPrompts] = useState({
    blagues: DEFAULT_BLAGUES_VIEUX,
    loi_nombres: DEFAULT_MESSAGES_LOI_NOMBRES,
    confirmation_rdv: DEFAULT_MESSAGES_CONFIRMATION_RDV,
    pause: DEFAULT_MESSAGES_PAUSE,
    system_message: "",
    welcome_message: ""
  });
  
  // Charger les prompts depuis l'API au montage
  useEffect(() => {
    const loadPrompts = async () => {
      try {
        const response = await authFetch(`${API}/api/sania/prompts`);
        if (response.ok) {
          const data = await response.json();
          setDynamicPrompts({
            blagues: data.blagues || DEFAULT_BLAGUES_VIEUX,
            loi_nombres: data.loi_nombres || DEFAULT_MESSAGES_LOI_NOMBRES,
            confirmation_rdv: data.confirmation_rdv || DEFAULT_MESSAGES_CONFIRMATION_RDV,
            pause: data.pause || DEFAULT_MESSAGES_PAUSE,
            system_message: data.system_message || "",
            welcome_message: data.welcome_message || ""
          });
          console.log("✅ Prompts SAN-IA chargés depuis l'API");
        }
      } catch (e) {
        console.log("⚠️ Utilisation des prompts par défaut", e);
      }
    };
    loadPrompts();
  }, []);
  
  // Utiliser les prompts dynamiques
  const BLAGUES_VIEUX = dynamicPrompts.blagues;
  const MESSAGES_LOI_NOMBRES = dynamicPrompts.loi_nombres;
  const MESSAGES_CONFIRMATION_RDV = dynamicPrompts.confirmation_rdv;
  const MESSAGES_PAUSE = dynamicPrompts.pause;
  
  // Analyse complète des performances
  const performanceAnalysis = useMemo(() => {
    const now = new Date();
    const today = now.toDateString();
    const hour = now.getHours();
    const dayOfWeek = now.getDay();
    const isVendredi = dayOfWeek === 5;
    const isMatin = hour >= 8 && hour < 12;
    const isApresMidi = hour >= 14 && hour < 17;
    const isFinJournee = hour >= 17 && hour < 19;
    
    // Calculer le début de la semaine (lundi)
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    startOfWeek.setHours(0, 0, 0, 0);
    
    // Calculer le début de la semaine dernière
    const startOfLastWeek = new Date(startOfWeek);
    startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);
    
    const endOfLastWeek = new Date(startOfWeek);
    endOfLastWeek.setDate(endOfLastWeek.getDate() - 1);
    
    // Jour de la semaine (1=lundi, 5=vendredi)
    const jourSemaine = ((now.getDay() + 6) % 7) + 1;
    
    // Stats Ringover
    const stats = ringoverStats || {};
    const totalAppels = (stats.outgoing || 0) + (stats.incoming || 0);
    const appelsDecroches = stats.answered || 0;
    const dureeEnLigneMin = Math.round((stats.totalDuration || 0) / 60);
    const tauxDecroche = totalAppels > 0 ? Math.round((appelsDecroches / totalAppels) * 100) : 0;
    const nrpCount = stats.nrp || 0;
    const voicemailCount = stats.voicemail || 0;
    
    // Clients data - utiliser les données personnalisées si un alias est défini
    const clients = personalizedDashboardData?.clients || [];
    
    // === CALCUL RDV PAR SEMAINE ===
    
    // RDV cette semaine
    const rdvCetteSemaine = clients.filter(c => {
      if (!c.datePriseRdv) return false;
      const date = parseDate(c.datePriseRdv);
      return date && date >= startOfWeek;
    }).length;
    
    // RDV semaine dernière (au même jour)
    const sameDayLastWeek = new Date(startOfLastWeek);
    sameDayLastWeek.setDate(sameDayLastWeek.getDate() + jourSemaine - 1);
    sameDayLastWeek.setHours(23, 59, 59, 999);
    
    const rdvSemaineDerniereAuMemeJour = clients.filter(c => {
      if (!c.datePriseRdv) return false;
      const date = parseDate(c.datePriseRdv);
      return date && date >= startOfLastWeek && date <= sameDayLastWeek;
    }).length;
    
    // RDV semaine dernière complète
    const rdvSemaineDerniereComplete = clients.filter(c => {
      if (!c.datePriseRdv) return false;
      const date = parseDate(c.datePriseRdv);
      return date && date >= startOfLastWeek && date < startOfWeek;
    }).length;
    
    // Calculer le record de RDV par semaine (toutes les semaines)
    const rdvParSemaine = {};
    clients.forEach(c => {
      if (!c.datePriseRdv) return;
      const date = parseDate(c.datePriseRdv);
      if (!date) return;
      // Calculer le lundi de cette semaine
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - ((date.getDay() + 6) % 7));
      const weekKey = weekStart.toISOString().split('T')[0];
      rdvParSemaine[weekKey] = (rdvParSemaine[weekKey] || 0) + 1;
    });
    
    const recordRDVSemaine = Math.max(...Object.values(rdvParSemaine), 0);
    
    // === LEADER DE LA SEMAINE ===
    const rdvParTelepro = {};
    const appelsParTelepro = {};
    
    clients.forEach(c => {
      if (!c.datePriseRdv || !c.telepro) return;
      const date = parseDate(c.datePriseRdv);
      if (!date || date < startOfWeek) return;
      rdvParTelepro[c.telepro] = (rdvParTelepro[c.telepro] || 0) + 1;
    });
    
    // Simuler les appels par télépro (basé sur les stats Ringover si disponible)
    if (stats.callsByUser) {
      Object.entries(stats.callsByUser).forEach(([user, count]) => {
        appelsParTelepro[user] = count;
      });
    }
    
    // Trouver le leader
    let leader = null;
    let maxRdv = 0;
    Object.entries(rdvParTelepro).forEach(([name, rdv]) => {
      if (rdv > maxRdv) {
        maxRdv = rdv;
        leader = {
          name,
          rdv,
          appels: appelsParTelepro[name] || Math.round(rdv * 15), // Estimation si pas de données
        };
      }
    });
    
    // === STATS PIPELINE ===
    
    // Dossiers transmis AMO semaine dernière
    const transmisAMO = clients.filter(c => {
      if (!c.dateTransfertAMO) return false;
      const date = parseDate(c.dateTransfertAMO);
      return date && date >= startOfLastWeek && date < startOfWeek;
    }).length;
    
    // VT planifiées à venir
    const vtAVenir = clients.filter(c => {
      if (!c.dateVtAmo) return false;
      const date = parseDate(c.dateVtAmo);
      return date && date >= now;
    }).length;
    
    // R2 semaine dernière
    const r2SemaineDerniere = clients.filter(c => {
      if (!c.dateR2) return false;
      const date = parseDate(c.dateR2);
      return date && date >= startOfLastWeek && date < startOfWeek;
    }).length;
    
    // R2 à venir
    const r2AVenir = clients.filter(c => {
      if (!c.dateR2) return false;
      const date = parseDate(c.dateR2);
      return date && date >= now;
    }).length;
    
    // === ALERTES ===
    
    // RDV aujourd'hui
    const rdvAujourdhui = clients.filter(c => {
      if (!c.datePriseRdv) return false;
      const date = parseDate(c.datePriseRdv);
      return date && date.toDateString() === today;
    }).length;
    
    // RDV hier
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const rdvHier = clients.filter(c => {
      if (!c.datePriseRdv) return false;
      const date = parseDate(c.datePriseRdv);
      return date && date.toDateString() === yesterday.toDateString();
    }).length;
    
    // CQ en retard
    const cqEnRetard = clients.filter(c => {
      if (!c.dateR1 || c.resultatAppel) return false;
      const dateR1 = parseDate(c.dateR1);
      if (!dateR1) return false;
      const diff = (now - dateR1) / (1000 * 60 * 60);
      return diff > 48;
    }).length;
    
    // RDV à confirmer
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const lundiProchain = new Date(now);
    lundiProchain.setDate(lundiProchain.getDate() + (8 - dayOfWeek) % 7);
    
    const rdvAConfirmer = clients.filter(c => {
      if (!c.dateR1 || c.rdvConfirme?.toUpperCase() === 'OUI') return false;
      const dateR1 = parseDate(c.dateR1);
      if (!dateR1) return false;
      const dateR1Str = dateR1.toDateString();
      if (isVendredi) {
        return dateR1Str === lundiProchain.toDateString();
      }
      return dateR1Str === tomorrow.toDateString();
    }).length;
    
    // === PERFORMANCE INDICATORS ===
    
    const isJourneeExceptionnelle = rdvAujourdhui >= 5;
    const isBonneJournee = rdvAujourdhui >= 3;
    const isBonneVeille = rdvHier >= 4;
    const isBonTempsEnLigne = dureeEnLigneMin >= 30;
    const needsPause = totalAppels >= 50; // Beaucoup d'appels = pause recommandée
    const isInactif = totalAppels < 5 && hour >= 10; // Peu d'appels après 10h
    const perdsVitesse = rdvCetteSemaine < rdvSemaineDerniereAuMemeJour;
    const enAvance = rdvCetteSemaine > rdvSemaineDerniereAuMemeJour;
    const procheRecord = rdvCetteSemaine >= recordRDVSemaine * 0.8 && recordRDVSemaine > 0;
    
    return {
      // Stats brutes
      totalAppels,
      appelsDecroches,
      tauxDecroche,
      dureeEnLigneMin,
      nrpCount,
      voicemailCount,
      rdvAujourdhui,
      rdvHier,
      cqEnRetard,
      rdvAConfirmer,
      
      // Stats semaine
      rdvCetteSemaine,
      rdvSemaineDerniereAuMemeJour,
      rdvSemaineDerniereComplete,
      recordRDVSemaine,
      jourSemaine,
      
      // Leader
      leader,
      
      // Pipeline
      transmisAMO,
      vtAVenir,
      r2SemaineDerniere,
      r2AVenir,
      
      // Contexte temporel
      isVendredi,
      isMatin,
      isApresMidi,
      isFinJournee,
      hour,
      
      // Performance flags
      isJourneeExceptionnelle,
      isBonneJournee,
      isBonneVeille,
      isBonTempsEnLigne,
      needsPause,
      isInactif,
      perdsVitesse,
      enAvance,
      procheRecord,
    };
  }, [ringoverStats, personalizedDashboardData]);
  
  // Générer les messages contextuels intelligents
  const contextualMessages = useMemo(() => {
    const messages = [];
    const perf = performanceAnalysis;
    const randomIndex = Math.floor(Date.now() / 60000); // Change toutes les minutes
    
    // === PRIORITÉ 1: ALERTES URGENTES ===
    
    // CQ en retard
    if (perf.cqEnRetard > 0) {
      messages.push({
        type: 'alerte',
        priority: 10,
        icon: AlertTriangle,
        iconColor: 'text-red-400',
        bgColor: 'from-red-900/40 to-rose-900/40',
        title: '⚠️ CQ EN RETARD !',
        message: `${perf.cqEnRetard} contrôles qualité en retard ! Tu es à la bourre, rattrape-toi vite ! 🏃‍♀️`,
      });
    }
    
    // Vendredi - RDV lundi
    if (perf.isVendredi && perf.rdvAConfirmer > 0) {
      messages.push({
        type: 'alerte',
        priority: 9,
        icon: Calendar,
        iconColor: 'text-orange-400',
        bgColor: 'from-orange-900/40 to-amber-900/40',
        title: '📅 VENDREDI - URGENT !',
        message: `${perf.rdvAConfirmer} RDV de LUNDI à confirmer avant de partir ! Pas de lapins ! 🐰`,
      });
    }
    
    // === PRIORITÉ 2: PERTE DE VITESSE ===
    
    if (perf.perdsVitesse && perf.rdvSemaineDerniereAuMemeJour > 0) {
      const msg = MESSAGES_PERTE_VITESSE[randomIndex % MESSAGES_PERTE_VITESSE.length]
        .replace('{current}', perf.rdvCetteSemaine)
        .replace('{last}', perf.rdvSemaineDerniereAuMemeJour);
      messages.push({
        type: 'alerte',
        priority: 8,
        icon: TrendingDown,
        iconColor: 'text-orange-400',
        bgColor: 'from-orange-900/30 to-red-900/30',
        title: '📉 ATTENTION RALENTISSEMENT !',
        message: msg,
      });
    }
    
    // === PRIORITÉ 3: FÉLICITATIONS ===
    
    // Proche du record
    if (perf.procheRecord) {
      const msg = MESSAGES_RECORD[randomIndex % MESSAGES_RECORD.length]
        .replace('{record}', perf.recordRDVSemaine)
        .replace('{current}', perf.rdvCetteSemaine);
      messages.push({
        type: 'felicitation',
        priority: 9,
        icon: Crown,
        iconColor: 'text-yellow-400',
        bgColor: 'from-yellow-900/40 to-amber-900/40',
        title: '👑 RECORD EN VUE !',
        message: msg,
      });
    }
    
    // En avance sur semaine dernière
    if (perf.enAvance && perf.rdvSemaineDerniereAuMemeJour > 0) {
      const msg = MESSAGES_BONNE_SEMAINE[randomIndex % MESSAGES_BONNE_SEMAINE.length]
        .replace('{current}', perf.rdvCetteSemaine)
        .replace('{last}', perf.rdvSemaineDerniereAuMemeJour);
      messages.push({
        type: 'felicitation',
        priority: 7,
        icon: Rocket,
        iconColor: 'text-green-400',
        bgColor: 'from-green-900/30 to-emerald-900/30',
        title: '🚀 EN AVANCE !',
        message: msg,
      });
    }
    
    // Journée exceptionnelle
    if (perf.isJourneeExceptionnelle) {
      messages.push({
        type: 'felicitation',
        priority: 8,
        icon: Trophy,
        iconColor: 'text-yellow-400',
        bgColor: 'from-yellow-900/40 to-amber-900/40',
        title: '🏆 JOURNÉE EXCEPTIONNELLE !',
        message: `${perf.rdvAujourdhui} RDV aujourd'hui ! Tu es une MACHINE ! 💰💰💰`,
      });
    }
    
    // Besoin de pause
    if (perf.needsPause) {
      messages.push({
        type: 'conseil',
        priority: 5,
        icon: Coffee,
        iconColor: 'text-amber-400',
        bgColor: 'from-amber-900/20 to-orange-900/20',
        title: '☕ PAUSE MÉRITÉE !',
        message: MESSAGES_PAUSE[randomIndex % MESSAGES_PAUSE.length],
      });
    }
    
    // Bon temps en ligne
    if (perf.isBonTempsEnLigne && !perf.needsPause) {
      messages.push({
        type: 'felicitation',
        priority: 6,
        icon: Phone,
        iconColor: 'text-green-400',
        bgColor: 'from-green-900/30 to-emerald-900/30',
        title: '📞 SUPER ENGAGEMENT !',
        message: `${perf.dureeEnLigneMin} min en ligne ! Les efforts paient TOUJOURS à la fin du mois ! 💵💵`,
      });
    }
    
    // === PRIORITÉ 4: LEADER DE LA SEMAINE ===
    
    if (perf.leader) {
      const msg = MESSAGES_LEADER[randomIndex % MESSAGES_LEADER.length]
        .replace('{name}', perf.leader.name)
        .replace('{rdv}', perf.leader.rdv)
        .replace('{appels}', perf.leader.appels);
      messages.push({
        type: 'info',
        priority: 4,
        icon: Crown,
        iconColor: 'text-purple-400',
        bgColor: 'from-purple-900/30 to-indigo-900/30',
        title: '👑 LEADER DE LA SEMAINE',
        message: msg,
      });
    }
    
    // === PRIORITÉ 5: STATS PIPELINE ===
    
    if (perf.transmisAMO > 0) {
      messages.push({
        type: 'info',
        priority: 3,
        icon: FileText,
        iconColor: 'text-blue-400',
        bgColor: 'from-blue-900/20 to-indigo-900/20',
        title: '📤 DOSSIERS TRANSMIS',
        message: MESSAGES_PIPELINE.transmisAMO[randomIndex % 2].replace('{count}', perf.transmisAMO),
      });
    }
    
    if (perf.vtAVenir > 0) {
      messages.push({
        type: 'info',
        priority: 2,
        icon: Eye,
        iconColor: 'text-cyan-400',
        bgColor: 'from-cyan-900/20 to-blue-900/20',
        title: '📅 VT À VENIR',
        message: MESSAGES_PIPELINE.vtAVenir[randomIndex % 2].replace('{count}', perf.vtAVenir),
      });
    }
    
    if (perf.r2AVenir > 0 || perf.r2SemaineDerniere > 0) {
      const r2Msg = perf.r2AVenir > 0 
        ? MESSAGES_PIPELINE.r2AVenir[randomIndex % 2].replace('{count}', perf.r2AVenir)
        : MESSAGES_PIPELINE.r2Semaine[randomIndex % 2].replace('{count}', perf.r2SemaineDerniere);
      messages.push({
        type: 'info',
        priority: 2,
        icon: Clipboard,
        iconColor: 'text-indigo-400',
        bgColor: 'from-indigo-900/20 to-purple-900/20',
        title: '📋 R2',
        message: r2Msg,
      });
    }
    
    // === PRIORITÉ 6: RAPPELS PÉDAGOGIQUES ===
    
    // Rappel CQ (aléatoire)
    if (randomIndex % 7 === 0) {
      messages.push({
        type: 'rappel',
        priority: 1,
        icon: Clipboard,
        iconColor: 'text-purple-400',
        bgColor: 'from-purple-900/20 to-pink-900/20',
        title: '📝 RAPPEL CQ',
        message: MESSAGES_CQ[randomIndex % MESSAGES_CQ.length],
      });
    }
    
    // Rappel confirmation (aléatoire)
    if (randomIndex % 5 === 0) {
      messages.push({
        type: 'rappel',
        priority: 1,
        icon: CheckCircle2,
        iconColor: 'text-green-400',
        bgColor: 'from-green-900/20 to-emerald-900/20',
        title: '✅ RAPPEL CONFIRMATION',
        message: MESSAGES_CONFIRMATION_RDV[randomIndex % MESSAGES_CONFIRMATION_RDV.length],
      });
    }
    
    // Loi des nombres (aléatoire)
    if (randomIndex % 3 === 0) {
      messages.push({
        type: 'motivation',
        priority: 1,
        icon: TrendingUp,
        iconColor: 'text-blue-400',
        bgColor: 'from-blue-900/20 to-cyan-900/20',
        title: '📊 LOI DES NOMBRES',
        message: MESSAGES_LOI_NOMBRES[randomIndex % MESSAGES_LOI_NOMBRES.length],
      });
    }
    
    // === PRIORITÉ 7: INACTIVITÉ ===
    
    if (perf.isInactif) {
      messages.push({
        type: 'alerte',
        priority: 6,
        icon: PhoneOff,
        iconColor: 'text-red-400',
        bgColor: 'from-red-900/30 to-orange-900/30',
        title: '😴 HÉ HO !',
        message: `Seulement ${perf.totalAppels} appels ? L'argent ne tombe pas du ciel ! Les NRP t'attendent ! 📞💰`,
      });
    }
    
    // NRP à rappeler
    if (perf.nrpCount >= 5 && perf.totalAppels < 20) {
      messages.push({
        type: 'conseil',
        priority: 3,
        icon: Phone,
        iconColor: 'text-blue-400',
        bgColor: 'from-blue-900/20 to-indigo-900/20',
        title: '💡 IDÉE',
        message: `${perf.nrpCount} NRP à rappeler ! Chaque rappel = nouvelle chance de RDV ! 📞`,
      });
    }
    
    // Message motivant par défaut
    if (messages.length < 3) {
      messages.push({
        type: 'motivation',
        priority: 0,
        icon: Heart,
        iconColor: 'text-pink-400',
        bgColor: 'from-pink-900/20 to-rose-900/20',
        title: '💪 SAN-IA VOUS DIT...',
        message: "Chaque appel peut changer une vie ! Tu fais un métier formidable ! 🌟",
      });
    }
    
    return messages.sort((a, b) => b.priority - a.priority);
  }, [performanceAnalysis]);
  
  // Fetch weather
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await authFetch('https://wttr.in/Paris?format=j1');
        if (response.ok) {
          const data = await response.json();
          const current = data.current_condition[0];
          setWeather({
            temp: current.temp_C,
            condition: current.weatherDesc[0].value.toLowerCase().includes('rain') ? 'rain' 
              : current.weatherDesc[0].value.toLowerCase().includes('cloud') ? 'clouds'
              : current.weatherDesc[0].value.toLowerCase().includes('snow') ? 'snow'
              : 'clear',
            description: current.weatherDesc[0].value,
          });
        }
      } catch (e) {
        setWeather({ temp: '8', condition: 'clouds', description: 'Nuageux' });
      }
    };
    fetchWeather();
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Update activity
  useEffect(() => {
    if (ringoverStats?.outgoing > 0 || ringoverStats?.incoming > 0) {
      setLastActivityTime(Date.now());
    }
  }, [ringoverStats?.outgoing, ringoverStats?.incoming]);
  
  // Auto-slide
  useEffect(() => {
    if (isPaused) return;
    const slideTime = contextualMessages[0]?.priority >= 8 ? 5000 : 7000;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % (contextualMessages.length + 3));
    }, slideTime);
    return () => clearInterval(interval);
  }, [isPaused, contextualMessages.length]);
  
  // Blagues
  useEffect(() => {
    const interval = setInterval(() => {
      setShowBlague(true);
      setCurrentBlague(Math.floor(Math.random() * BLAGUES_VIEUX.length));
      setTimeout(() => setShowBlague(false), 10000);
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  const perf = performanceAnalysis;
  
  // Construire tous les slides
  const allSlides = useMemo(() => {
    const slides = [];
    
    // Slide 1: Stats compactes (personnalisées si alias défini)
    const statsTitle = isPersonalized 
      ? `📊 Mes Stats - ${userTelepro}` 
      : '📊 Météo des Performances';
    
    slides.push({
      id: 'stats',
      title: statsTitle,
      bgClass: isPersonalized ? 'from-indigo-900/40 to-purple-900/40' : 'from-slate-800 to-slate-900',
      content: (
        <div className="flex items-center gap-3 text-sm flex-wrap">
          {isPersonalized && (
            <div className="flex items-center gap-1 bg-indigo-500/30 px-2 py-1 rounded-full border border-indigo-400/30">
              <Users className="w-3 h-3 text-indigo-400" />
              <span className="text-indigo-300 text-xs font-medium">Perso</span>
            </div>
          )}
          <div className="flex items-center gap-1 bg-blue-500/20 px-2 py-1 rounded-full">
            <Phone className="w-3 h-3 text-blue-400" />
            <span className="text-white font-bold">{perf.totalAppels}</span>
            <span className="text-slate-400 text-xs">appels</span>
          </div>
          <div className="flex items-center gap-1 bg-green-500/20 px-2 py-1 rounded-full">
            <Target className="w-3 h-3 text-green-400" />
            <span className="text-white font-bold">{perf.tauxDecroche}%</span>
            <span className="text-slate-400 text-xs">décrochés</span>
          </div>
          <div className="flex items-center gap-1 bg-purple-500/20 px-2 py-1 rounded-full">
            <Calendar className="w-3 h-3 text-purple-400" />
            <span className="text-white font-bold">{perf.rdvAujourdhui}</span>
            <span className="text-slate-400 text-xs">RDV jour</span>
          </div>
          <div className="flex items-center gap-1 bg-amber-500/20 px-2 py-1 rounded-full">
            <Trophy className="w-3 h-3 text-amber-400" />
            <span className="text-white font-bold">{perf.rdvCetteSemaine}</span>
            <span className="text-slate-400 text-xs">RDV sem.</span>
          </div>
          {perf.rdvSemaineDerniereAuMemeJour > 0 && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${
              perf.perdsVitesse ? 'bg-red-500/20' : 'bg-green-500/20'
            }`}>
              {perf.perdsVitesse ? (
                <TrendingDown className="w-3 h-3 text-red-400" />
              ) : (
                <TrendingUp className="w-3 h-3 text-green-400" />
              )}
              <span className={`text-xs ${perf.perdsVitesse ? 'text-red-400' : 'text-green-400'}`}>
                vs {perf.rdvSemaineDerniereAuMemeJour} sem. dern.
              </span>
            </div>
          )}
        </div>
      ),
    });
    
    // Messages contextuels (max 4)
    contextualMessages.slice(0, 4).forEach((msg, i) => {
      slides.push({
        id: `context-${i}`,
        title: msg.title,
        bgClass: `bg-gradient-to-r ${msg.bgColor}`,
        icon: msg.icon,
        iconColor: msg.iconColor,
        content: (
          <div className="flex items-center gap-2">
            <span className="text-slate-200 text-sm">{msg.message}</span>
          </div>
        ),
      });
    });
    
    // Slide comparatif semaines
    slides.push({
      id: 'comparatif',
      title: '📈 Comparatif Semaines',
      bgClass: 'from-indigo-900/20 to-purple-900/20',
      content: (
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-slate-400">Cette semaine:</span>
            <span className="text-white font-bold text-lg">{perf.rdvCetteSemaine}</span>
            <span className="text-slate-500">RDV</span>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-500" />
          <div className="flex items-center gap-2">
            <span className="text-slate-400">Sem. dernière:</span>
            <span className="text-white font-bold">{perf.rdvSemaineDerniereComplete}</span>
          </div>
          <div className="flex items-center gap-2 bg-yellow-500/20 px-2 py-1 rounded-full">
            <Medal className="w-3 h-3 text-yellow-400" />
            <span className="text-yellow-400 text-xs">Record: {perf.recordRDVSemaine}</span>
          </div>
        </div>
      ),
    });
    
    // Top Télépros
    slides.push({
      id: 'top',
      title: '🏆 Top Télépros',
      bgClass: 'from-amber-900/20 to-yellow-900/20',
      content: telepros && telepros.length > 0 ? (
        <div className="flex items-center gap-3 text-sm">
          {telepros.slice(0, 3).map((t, i) => (
            <div key={t.name} className="flex items-center gap-2">
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                i === 0 ? 'bg-yellow-500 text-black' : 
                i === 1 ? 'bg-slate-400 text-black' : 
                'bg-amber-700 text-white'
              }`}>
                {i + 1}
              </span>
              <span className="text-white font-medium">{t.name?.split(' ')[0]}</span>
              <span className="text-blue-400 font-bold">{t.count}</span>
            </div>
          ))}
        </div>
      ) : <span className="text-slate-400">Chargement...</span>,
    });
    
    // Météo
    slides.push({
      id: 'meteo',
      title: '☕ Pause info',
      bgClass: 'from-cyan-900/20 to-blue-900/20',
      content: (
        <div className="flex items-center gap-3 text-sm">
          {weather && (
            <div className="flex items-center gap-1 bg-blue-500/20 px-2 py-1 rounded-full">
              <WeatherIcon condition={weather.condition} className="w-3 h-3 text-blue-400" />
              <span className="text-blue-300 text-xs">Paris {weather.temp}°C</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Coffee className="w-3 h-3 text-amber-400" />
            <span className="text-slate-300 text-xs">
              {perf.hour < 12 ? 'Bonne matinée !' : 
               perf.hour < 14 ? 'Bon appétit !' :
               perf.hour < 18 ? 'Bon après-midi !' : 'Bonne soirée !'}
            </span>
          </div>
          {perf.isVendredi && (
            <div className="flex items-center gap-1 bg-green-500/20 px-2 py-1 rounded-full">
              <Gift className="w-3 h-3 text-green-400" />
              <span className="text-green-300 text-xs">Bon week-end !</span>
            </div>
          )}
        </div>
      ),
    });
    
    return slides;
  }, [perf, contextualMessages, telepros, weather, isPersonalized, userTelepro]);
  
  const currentSlideData = allSlides[currentSlide % allSlides.length];
  const isUrgent = contextualMessages[0]?.priority >= 8;
  
  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 12000);
  };
  
  return (
    <div className={`relative bg-gradient-to-r ${currentSlideData?.bgClass || 'from-slate-800 to-slate-900'} border ${isUrgent ? 'border-orange-500/50 shadow-lg shadow-orange-500/10' : 'border-slate-700'} rounded-xl overflow-hidden mb-6 transition-all duration-500`}>
      {/* Blague overlay */}
      {showBlague && (
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/98 via-indigo-900/98 to-purple-900/98 z-20 flex items-center justify-center px-6 animate-fade-in">
          <div className="flex items-center gap-4 max-w-4xl">
            <img src={SANIA_LOGO} alt="SAN-IA" className="w-16 h-16 rounded-xl animate-bounce shadow-lg shadow-purple-500/30" />
            <div>
              <p className="text-purple-300 text-xs uppercase tracking-wider mb-1">😂 Blague du moment</p>
              <p className="text-white text-base">{BLAGUES_VIEUX[currentBlague]}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Urgent indicator */}
      {isUrgent && !showBlague && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-red-500 to-orange-500 animate-pulse" />
      )}
      
      {/* Main banner */}
      <div className="flex items-center px-4 py-2.5 gap-3">
        {/* Logo */}
        <div className="flex-shrink-0 relative">
          <img 
            src={SANIA_LOGO} 
            alt="SAN-IA" 
            className={`w-11 h-11 rounded-xl border-2 ${isUrgent ? 'border-orange-500/70 shadow-orange-500/30' : 'border-blue-500/50 shadow-blue-500/20'} shadow-lg`}
          />
          {isUrgent && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping" />
          )}
        </div>
        
        {/* Content */}
        <div className="flex-grow overflow-hidden min-w-0">
          <span className={`text-xs uppercase tracking-wider font-semibold ${isUrgent ? 'text-orange-400' : 'text-blue-400'}`}>
            {currentSlideData?.title}
          </span>
          <div className="mt-0.5">{currentSlideData?.content}</div>
        </div>
        
        {/* Navigation */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button onClick={() => goToSlide((currentSlide - 1 + allSlides.length) % allSlides.length)} className="p-1 text-slate-400 hover:text-white">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex gap-0.5">
            {allSlides.slice(0, 7).map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  i === (currentSlide % allSlides.length) ? (isUrgent ? 'bg-orange-500 w-3' : 'bg-blue-500 w-3') : 'bg-slate-600 hover:bg-slate-500'
                }`}
              />
            ))}
          </div>
          <button onClick={() => goToSlide((currentSlide + 1) % allSlides.length)} className="p-1 text-slate-400 hover:text-white">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button onClick={() => setIsPaused(!isPaused)} className="p-1 text-slate-400 hover:text-white ml-1">
            {isPaused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
          </button>
        </div>
      </div>
      
      {/* Progress */}
      <div className="h-0.5 bg-slate-700/50">
        <div 
          className={`h-full ${isUrgent ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'bg-gradient-to-r from-blue-500 to-purple-500'}`}
          style={{ animation: isPaused ? 'none' : `progress ${isUrgent ? '5' : '7'}s linear infinite` }}
        />
      </div>
      
      <style>{`
        @keyframes progress { from { width: 0%; } to { width: 100%; } }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
      `}</style>
    </div>
  );
}

function parseDate(dateStr) {
  if (!dateStr || !dateStr.trim()) return null;
  const formats = [
    { regex: /^(\d{2})\/(\d{2})\/(\d{4})$/, fn: (m) => new Date(m[3], m[2] - 1, m[1]) },
    { regex: /^(\d{2})\/(\d{2})\/(\d{2})$/, fn: (m) => new Date(2000 + parseInt(m[3]), m[2] - 1, m[1]) },
    { regex: /^(\d{4})-(\d{2})-(\d{2})$/, fn: (m) => new Date(m[1], m[2] - 1, m[3]) },
  ];
  for (const { regex, fn } of formats) {
    const match = dateStr.trim().match(regex);
    if (match) {
      const date = fn(match);
      if (!isNaN(date.getTime())) return date;
    }
  }
  return null;
}

export default SanIABanner;
